import axios, { type AxiosProgressEvent, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/lib/auth/authStore';
import { ApiError } from './errors';
import {
  attachAuthInterceptor,
  attachRefreshInterceptor,
  attachRetryInterceptor,
  buildListParams,
  type ListParams,
} from './interceptors';
import type { AuthResponse, ListEnvelope } from './types';

const BASE_URL: string = import.meta.env.VITE_API_URL ?? '';
const DEFAULT_TIMEOUT_MS = 30_000;

export const axiosClient = axios.create({
  baseURL: BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
});

attachAuthInterceptor(axiosClient);
attachRetryInterceptor(axiosClient);
attachRefreshInterceptor(axiosClient, refreshAccessToken, onAuthFailure);

/**
 * Single-flight token refresh. Used by the response interceptor.
 *
 * NOTE: depends on backend endpoint `POST /auth/refresh` which is being added
 * as part of the Phase 1.3 work in plan. Until that ships, a 401 will fail
 * here and the user will be logged out — same behaviour as v1 today.
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post<AuthResponse>(
      `${BASE_URL}/auth/refresh`,
      { refresh_token: refreshToken },
      { timeout: DEFAULT_TIMEOUT_MS },
    );
    useAuthStore.getState().setSession({
      token: data.access_token,
      refreshToken: data.refresh_token ?? refreshToken,
      user: data.user,
      role: data.role,
    });
    return data.access_token;
  } catch {
    return null;
  }
}

function onAuthFailure(): void {
  useAuthStore.getState().logout();
  // Soft redirect — router catches via ProtectedRoute too, but this guarantees
  // anywhere outside of a route (e.g. from a background mutation).
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
}

// ── Public API surface ────────────────────────────────────────────────

type RequestConfig = Omit<AxiosRequestConfig, 'method' | 'url' | 'data'>;

async function unwrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  try {
    const res = await promise;
    return res.data;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (axios.isAxiosError(err)) throw ApiError.fromAxios(err);
    throw err;
  }
}

export interface UploadOptions {
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
  /** Override field name (default: "file"). */
  fieldName?: string;
}

export interface UploadResponse {
  /** Absolute URL the backend assembles when present (Phase 1.4 backend change). */
  url?: string;
  /** Filename — always returned by current backend for v1 retrocompat. */
  filename: string;
}

export const api = {
  get: <T>(path: string, config?: RequestConfig) =>
    unwrap<T>(axiosClient.get<T>(path, config)),

  post: <T>(path: string, body?: unknown, config?: RequestConfig) =>
    unwrap<T>(axiosClient.post<T>(path, body, config)),

  put: <T>(path: string, body?: unknown, config?: RequestConfig) =>
    unwrap<T>(axiosClient.put<T>(path, body, config)),

  patch: <T>(path: string, body?: unknown, config?: RequestConfig) =>
    unwrap<T>(axiosClient.patch<T>(path, body, config)),

  delete: <T = void>(path: string, config?: RequestConfig) =>
    unwrap<T>(axiosClient.delete<T>(path, config)),

  /**
   * List endpoint helper. Sends params in the ox-platform format
   * (range/sort/filter as JSON strings) and unwraps `{ data: { items, total } }`.
   */
  list: async <T>(
    path: string,
    params?: ListParams,
    config?: RequestConfig,
  ): Promise<{ items: T[]; total: number }> => {
    const env = await unwrap<ListEnvelope<T>>(
      axiosClient.get<ListEnvelope<T>>(path, {
        ...config,
        params: { ...buildListParams(params), ...(config?.params ?? {}) },
      }),
    );
    return env.data;
  },

  /**
   * Upload a single file to `POST /upload` as multipart/form-data.
   *
   * Backend currently returns `{ filename }`. After the Phase 1.4 backend
   * change it will also return `{ url }` — both fields are supported here so
   * v2 callers can prefer `url` when available and fall back to constructing
   * it from VITE_API_FILE_URL if needed.
   */
  upload: (file: File | Blob, opts: UploadOptions = {}): Promise<UploadResponse> => {
    const form = new FormData();
    form.append(opts.fieldName ?? 'file', file);

    return unwrap<UploadResponse>(
      axiosClient.post<UploadResponse>('/upload', form, {
        signal: opts.signal,
        onUploadProgress: opts.onProgress
          ? (evt: AxiosProgressEvent) => {
              if (!evt.total) return;
              opts.onProgress!(Math.round((evt.loaded / evt.total) * 100));
            }
          : undefined,
      }),
    );
  },
};

// Re-export for convenience.
export { ApiError } from './errors';
export type { ListParams } from './interceptors';
